import React, { useState, useEffect } from 'react';
import api from './api';

const MonthlyPayrollEntry = () => {
    const [workers, setWorkers] = useState([]);
    const [payrollData, setPayrollData] = useState({});
    const [processRates, setProcessRates] = useState([]); 
    
    // Default standard hours per month
    const [standardHours, setStandardHours] = useState(286);
    
    const getCurrentMonth = () => {
        const date = new Date();
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    };
    
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);

    // --- MV MODAL STATE ---
    const [mvModalWorker, setMvModalWorker] = useState(null);
    const [tempMvData, setTempMvData] = useState({});

    useEffect(() => {
        loadData();
    }, [selectedMonth]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [workerRes, payrollRes, processRes] = await Promise.all([
                api.get('/factory-workers'),
                api.get(`/monthly-payroll/${selectedMonth}`),
                api.get('/hr-masters/process-rates') 
            ]);

            const allWorkers = Array.isArray(workerRes.data) ? workerRes.data : (workerRes.data.data || []);
            const existingRecords = Array.isArray(payrollRes.data) ? payrollRes.data : (payrollRes.data.data || []);
            setProcessRates(processRes.data.data || processRes.data || []);
            setWorkers(allWorkers);

            const initialData = {};
            allWorkers.forEach(w => {
                const existing = existingRecords.find(r => r.workerId?._id === w._id);
                initialData[w._id] = {
                    presentHours: existing?.presentHours || '',
                    totalPieces: existing?.totalPieces || '',
                    bonus: existing?.bonus || '',
                    adjustments: existing?.adjustments || '',
                    advances: existing?.advances || '',
                    leaveDeduction: existing?.leaveDeduction || '',
                    mvDetails: existing?.mvDetails || []
                };
            });
            setPayrollData(initialData);

        } catch (err) {
            console.error("Failed to load payroll grid:", err);
            alert("Error loading data.");
        } finally {
            setLoading(false);
        }
    };

    // --- 🧮 LIVE CALCULATION ENGINE ---
    const calculateActual = (worker, pd) => {
        let actual = 0;
        if (worker.salaryType === 'F') {
            actual = ((Number(worker.basicSalary) || 0) / Number(standardHours)) * (Number(pd.presentHours) || 0);
        } else if (worker.salaryType === 'V') {
            actual = (Number(pd.totalPieces) || 0) * (Number(worker.defaultPieceRate) || 0);
        } else if (worker.salaryType === 'MV') {
            (pd.mvDetails || []).forEach(item => {
                const procId = typeof item.processId === 'object' ? item.processId._id : item.processId;
                const proc = processRates.find(p => p._id === procId);
                const rate = proc ? Number(proc.defaultRate) : 0;
                actual += (Number(item.pieceCount) || 0) * rate;
            });
        }
        return actual;
    };

    const calculateLiveValues = (worker, pd) => {
        const actual = calculateActual(worker, pd);
        const gross = actual + (Number(pd.bonus) || 0) + (Number(pd.adjustments) || 0);
        
        let ptAmount = 0;
        let esicAmount = 0;
        let pfAmount = 0;

        // 🛑 APPLY TAXES ONLY IF MODE IS "BANK"
        if (worker.paymentMode === 'Bank') {
            if (worker.hasPT && gross > 0) {
                ptAmount = 200; // Standard PT Deduction
            }
            if (worker.hasPF && gross > 0) {
                // pfAmount = Math.round(actual * 0.12); // Future PF Use
            }
            if (worker.hasESIC && gross > 0) {
                // esicAmount = Math.round(gross * 0.0075); // Future ESIC Use
            }
        }

        const net = gross - (Number(pd.advances) || 0) - (Number(pd.leaveDeduction) || 0) - ptAmount - esicAmount - pfAmount;
        
        return { 
            actual: Math.round(actual),
            ptAmount, 
            esicAmount,
            pfAmount,
            net: Math.round(net) 
        };
    };

    // --- STANDARD INPUT HANDLER ---
    const handleInputChange = (workerId, field, value) => {
        setPayrollData(prev => ({
            ...prev,
            [workerId]: { ...prev[workerId], [field]: value }
        }));
    };

    // --- MV MODAL HANDLERS ---
    const openMvModal = (worker) => {
        setMvModalWorker(worker);
        const currentMv = payrollData[worker._id]?.mvDetails || [];
        const temp = {};
        currentMv.forEach(item => {
            const procId = item.processId?._id || item.processId;
            temp[procId] = item.pieceCount;
        });
        setTempMvData(temp);
    };

    const handleTempMvChange = (processId, value) => {
        setTempMvData(prev => ({ ...prev, [processId]: value }));
    };

    const saveMvModal = () => {
        const formattedMvDetails = Object.keys(tempMvData)
            .map(procId => ({ processId: procId, pieceCount: Number(tempMvData[procId]) || 0 }))
            .filter(item => item.pieceCount > 0);

        setPayrollData(prev => ({
            ...prev,
            [mvModalWorker._id]: { ...prev[mvModalWorker._id], mvDetails: formattedMvDetails }
        }));
        setMvModalWorker(null);
    };

    // --- ROW SAVE HANDLER ---
    const handleSaveRow = async (workerId) => {
        try {
            setSavingId(workerId);
            const worker = workers.find(w => w._id === workerId);
            const pd = payrollData[workerId];
            
            const liveCalc = calculateLiveValues(worker, pd);

            const dataToSave = {
                workerId,
                monthYear: selectedMonth,
                actualCalculated: liveCalc.actual,
                ptAmount: liveCalc.ptAmount,
                esicAmount: liveCalc.esicAmount,
                pfAmount: liveCalc.pfAmount,
                netPayable: liveCalc.net, 
                ...pd
            };
            
            Object.keys(dataToSave).forEach(key => {
                if (dataToSave[key] === '') dataToSave[key] = 0;
            });

            await api.post('/monthly-payroll/save', dataToSave);
            setTimeout(() => setSavingId(null), 1000); 
        } catch (err) {
            alert("Error saving row!");
            setSavingId(null);
        }
    };

    return (
        <div className="container-fluid mt-4 mb-5 px-4 position-relative">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-primary">📝 Monthly Payroll Entry</h3>
                
                <div className="d-flex gap-3">
                    <div className="d-flex align-items-center bg-white p-2 rounded shadow-sm border">
                        <label className="fw-bold me-2 mb-0 text-secondary small">Std. Hours:</label>
                        <input 
                            type="number" 
                            className="form-control form-control-sm border-secondary fw-bold text-center" 
                            style={{width: '70px'}}
                            value={standardHours}
                            onChange={(e) => setStandardHours(e.target.value)}
                        />
                    </div>
                    <div className="d-flex align-items-center bg-white p-2 rounded shadow-sm border">
                        <label className="fw-bold me-2 mb-0 text-secondary small">Month:</label>
                        <input 
                            type="month" 
                            className="form-control form-control-sm border-primary fw-bold" 
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="card shadow-sm border-0">
                <div className="table-responsive">
                    <table className="table table-hover table-bordered align-middle mb-0" style={{ minWidth: '1300px' }}>
                        <thead className="table-dark text-center align-middle">
                            <tr>
                                <th rowSpan="2" className="text-start">Worker Name</th>
                                <th rowSpan="2">Type</th>
                                <th colSpan="2" className="bg-primary border-light">Production / Attendance</th>
                                <th colSpan="4" className="bg-secondary border-light">Financials (₹)</th>
                                <th rowSpan="2" className="bg-success text-white">Live Net (₹)</th>
                                <th rowSpan="2">Action</th>
                            </tr>
                            <tr>
                                <th className="bg-light text-dark small">Hours (F)</th>
                                <th className="bg-light text-dark small">Pieces (V/MV)</th>
                                <th className="bg-light text-dark small">Bonus</th>
                                <th className="bg-light text-dark small">Adj. (+/-)</th>
                                <th className="bg-light text-dark small">Upad (Adv)</th>
                                <th className="bg-light text-dark small">Leave Ded.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" className="text-center py-5 fw-bold text-muted">Loading Data...</td></tr>
                            ) : workers.length === 0 ? (
                                <tr><td colSpan="10" className="text-center py-4 text-muted">No active factory workers found.</td></tr>
                            ) : (
                                workers.map(w => {
                                    const pd = payrollData[w._id] || {};
                                    
                                    const totalMvPieces = w.salaryType === 'MV' 
                                        ? (pd.mvDetails || []).reduce((sum, item) => sum + (Number(item.pieceCount) || 0), 0)
                                        : 0;
                                        
                                    // ⚡ THE MAGIC NUMBERS
                                    const liveCalc = calculateLiveValues(w, pd);

                                    return (
                                        <tr key={w._id}>
                                            <td className="fw-bold text-primary text-start">
                                                {w.name} <br/>
                                                <small className="text-muted fw-normal me-2">{w.department?.name || 'No Dept'}</small>
                                                <span className={`badge ${w.paymentMode === 'Bank' ? 'bg-info text-dark' : 'bg-secondary'} px-1 py-0`} style={{fontSize: '0.65rem'}}>
                                                    {w.paymentMode || 'Cash'}
                                                </span>
                                            </td>
                                            
                                            <td className="text-center fw-bold">
                                                <span className={`badge ${w.salaryType === 'F' ? 'bg-primary' : w.salaryType === 'V' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {w.salaryType}
                                                </span>
                                            </td>

                                            <td>
                                                <input type="number" className="form-control form-control-sm text-center" 
                                                    value={pd.presentHours} onChange={(e) => handleInputChange(w._id, 'presentHours', e.target.value)}
                                                    disabled={w.salaryType !== 'F'} placeholder={w.salaryType === 'F' ? "0" : "-"}
                                                />
                                            </td>
                                            <td>
                                                {w.salaryType === 'MV' ? (
                                                    <button className="btn btn-sm btn-outline-warning w-100 fw-bold" onClick={() => openMvModal(w)}>
                                                        💎 MV Entry {totalMvPieces > 0 ? `(${totalMvPieces})` : ''}
                                                    </button>
                                                ) : (
                                                    <input type="number" className="form-control form-control-sm text-center" 
                                                        value={pd.totalPieces} onChange={(e) => handleInputChange(w._id, 'totalPieces', e.target.value)}
                                                        disabled={w.salaryType !== 'V'} placeholder={w.salaryType === 'V' ? "0" : "-"}
                                                    />
                                                )}
                                            </td>

                                            <td>
                                                <input type="number" className="form-control form-control-sm text-end text-success fw-bold" 
                                                    value={pd.bonus} onChange={(e) => handleInputChange(w._id, 'bonus', e.target.value)} placeholder="0" />
                                            </td>
                                            <td>
                                                <input type="number" className="form-control form-control-sm text-end" 
                                                    value={pd.adjustments} onChange={(e) => handleInputChange(w._id, 'adjustments', e.target.value)} placeholder="0" />
                                            </td>
                                            <td>
                                                <input type="number" className="form-control form-control-sm text-end text-danger fw-bold" 
                                                    value={pd.advances} onChange={(e) => handleInputChange(w._id, 'advances', e.target.value)} placeholder="0" />
                                            </td>
                                            <td>
                                                <input type="number" className="form-control form-control-sm text-end text-danger" 
                                                    value={pd.leaveDeduction} onChange={(e) => handleInputChange(w._id, 'leaveDeduction', e.target.value)} placeholder="0" />
                                            </td>

                                            {/* ⚡ LIVE NET DISPLAY WITH PT */}
                                            <td className="text-end bg-light align-middle">
                                                <span className={`fs-5 fw-bold ${liveCalc.net < 0 ? 'text-danger' : 'text-success'}`}>
                                                    ₹{liveCalc.net.toLocaleString()}
                                                </span>
                                                {liveCalc.ptAmount > 0 && (
                                                    <div className="text-danger small fw-bold mt-1" style={{fontSize: '0.7rem'}}>
                                                        -₹{liveCalc.ptAmount} (PT)
                                                    </div>
                                                )}
                                            </td>

                                            <td className="text-center">
                                                <button 
                                                    className={`btn btn-sm fw-bold w-100 ${savingId === w._id ? 'btn-success' : 'btn-primary'}`}
                                                    onClick={() => handleSaveRow(w._id)}
                                                    disabled={savingId === w._id}
                                                >
                                                    {savingId === w._id ? '✅ Saved' : '💾 Save'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- MV MODAL --- */}
            {mvModalWorker && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg border-warning">
                            <div className="modal-header bg-warning text-dark">
                                <h5 className="modal-title fw-bold">💎 MV Entry: {mvModalWorker.name}</h5>
                                <button type="button" className="btn-close" onClick={() => setMvModalWorker(null)}></button>
                            </div>
                            <div className="modal-body bg-light">
                                <table className="table table-sm table-bordered bg-white">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Process / Size</th>
                                            <th className="text-center">Rate</th>
                                            <th className="text-end" style={{width: '120px'}}>Pieces Done</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {processRates.filter(p => (p.departmentId?._id || p.departmentId) === mvModalWorker.department?._id).map(proc => (
                                            <tr key={proc._id}>
                                                <td className="align-middle fw-bold text-primary">{proc.processName}</td>
                                                <td className="align-middle text-center text-success fw-bold">₹{proc.defaultRate}</td>
                                                <td>
                                                    <input 
                                                        type="number" 
                                                        className="form-control form-control-sm text-end fw-bold" 
                                                        placeholder="0"
                                                        value={tempMvData[proc._id] || ''}
                                                        onChange={(e) => handleTempMvChange(proc._id, e.target.value)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer bg-light">
                                <button type="button" className="btn btn-secondary" onClick={() => setMvModalWorker(null)}>Cancel</button>
                                <button type="button" className="btn btn-warning fw-bold text-dark" onClick={saveMvModal}>Save & Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
        </div>
    );
};

export default MonthlyPayrollEntry;